import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { Loader2, RefreshCw } from "lucide-react";

import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { PageLoader } from "../../components/ui/spinner";
import { SeriesTooltip } from "../../components/ui/chart-tooltip";
import { getSessionAnalytics, refreshSessionAnalytics } from "../../lib/api";
import { SessionsModeToggle } from "./SessionsModeToggle";

/**
 * Session Analytics — therapy session volume and outcomes across the fleet.
 * FleetAdmin only; the route and the service both gate on that.
 */

// Outcome colours, validated against the chart surface for the lightness band,
// chroma floor, colourblind separation and 3:1 contrast — do not substitute by
// eye. The three are a categorical set in fixed order; `unknown` is deliberately
// as legible as the other two rather than a faint grey, because it is a real
// answer ("we cannot tell") and not an absence.
const OUTCOMES = [
  { key: "completed", label: "Completed", color: "#0d9488" },
  { key: "incomplete", label: "Incomplete", color: "#ea580c" },
  { key: "unknown", label: "Unknown", color: "#6366f1" },
];

const POLL_LIMIT = 5;

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoIso(days) {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() - days);
  return date.toISOString().slice(0, 10);
}

function formatStamp(iso) {
  if (!iso) return "never";
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? "unknown" : parsed.toLocaleString();
}

function StatTile({ label, value, color, hint }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-5 py-4">
      <div className="flex items-center gap-2">
        {color && (
          <span
            className="inline-block h-2.5 w-2.5 rounded-sm shrink-0"
            style={{ backgroundColor: color }}
            aria-hidden="true"
          />
        )}
        <p className="text-sm font-medium text-slate-600">{label}</p>
      </div>
      <p className="mt-1 text-2xl font-bold text-slate-900 tabular-nums">{value}</p>
      {hint && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

export default function SessionAnalyticsPage() {
  const [fromDate, setFromDate] = useState(daysAgoIso(29));
  const [toDate, setToDate] = useState(todayIso());
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshNote, setRefreshNote] = useState("");

  // Changing the dates quickly must not let a slower earlier answer land last.
  const latestRequest = useRef(0);

  const load = useCallback(async () => {
    const request = ++latestRequest.current;
    const isCurrent = () => request === latestRequest.current;

    setLoadError("");
    const result = await getSessionAnalytics({ from: fromDate, to: toDate });
    if (!isCurrent()) return;

    if (!result) {
      // Deliberately an error, not an empty chart: zero sessions is a plausible
      // number, so a silent zero would be indistinguishable from a real answer.
      setLoadError("Could not load session analytics. Please try again.");
    } else {
      setData(result);
    }
    setIsInitialLoading(false);
  }, [fromDate, toDate]);

  useEffect(() => {
    load();
  }, [load]);

  const onRefresh = async () => {
    setRefreshing(true);
    setRefreshNote("");
    try {
      let result = await refreshSessionAnalytics({ from: fromDate, to: toDate });

      for (let attempt = 0; result?.running && attempt < POLL_LIMIT; attempt += 1) {
        // Copied out of `result` before the closure, so the timer does not
        // capture a variable the loop reassigns.
        const { queryExecutionId, pollAfterMs } = result;
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, pollAfterMs || 2000));
        // eslint-disable-next-line no-await-in-loop
        result = await refreshSessionAnalytics({ from: fromDate, to: toDate, queryExecutionId });
      }

      if (result?.running) {
        setRefreshNote("Still running — the figures below are the last completed run.");
      } else if (result) {
        setData(result);
      }
    } catch (err) {
      setRefreshNote(
        err?.retryAfterSeconds
          ? `${err.message} (about ${err.retryAfterSeconds}s)`
          : err?.message || "Refresh failed.",
      );
    } finally {
      setRefreshing(false);
    }
  };

  if (isInitialLoading) return <PageLoader />;

  const totals = data?.totals;
  const series = data?.series ?? [];
  const unknownPct = Math.round((data?.coverage?.unknownShare ?? 0) * 1000) / 10;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-slate-900">Session Analytics</h1>
        <SessionsModeToggle active="analytics" />
      </div>

      {/* Controls */}
      <div className="bg-white border border-slate-200 rounded-xl px-5 py-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <Label htmlFor="analyticsFrom" className="text-sm font-medium text-slate-700">
              From
            </Label>
            <input
              id="analyticsFrom"
              type="date"
              value={fromDate}
              max={toDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="mt-1 block rounded-md border border-slate-300 px-3 py-1.5 text-sm"
            />
          </div>
          <div>
            <Label htmlFor="analyticsTo" className="text-sm font-medium text-slate-700">
              To
            </Label>
            <input
              id="analyticsTo"
              type="date"
              value={toDate}
              min={fromDate}
              max={todayIso()}
              onChange={(e) => setToDate(e.target.value)}
              className="mt-1 block rounded-md border border-slate-300 px-3 py-1.5 text-sm"
            />
          </div>

          <Button
            onClick={onRefresh}
            disabled={refreshing}
            className="bg-[#08148C] text-white hover:bg-[#08148C]/90 gap-2"
          >
            {refreshing
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <RefreshCw className="h-4 w-4" />}
            {refreshing ? "Refreshing…" : "Refresh"}
          </Button>

          <div className="ml-auto text-right">
            <p className={`text-xs ${data?.stale ? "text-amber-600" : "text-slate-500"}`}>
              Updated {formatStamp(data?.generatedAt)}
              {data?.stale ? " — showing the last completed run" : ""}
            </p>
            {refreshNote && (
              <p role="status" className="text-xs text-amber-600 mt-0.5">{refreshNote}</p>
            )}
          </div>
        </div>
      </div>

      {loadError && (
        <div role="alert" className="bg-white border border-red-200 rounded-xl px-5 py-4">
          <p className="text-sm font-medium text-red-600">{loadError}</p>
        </div>
      )}

      {data?.noData && !loadError && (
        <div className="bg-white border border-slate-200 rounded-xl px-5 py-10 text-center">
          <p className="text-sm text-slate-500">
            No analytics have been produced for this range yet.
          </p>
        </div>
      )}

      {!data?.noData && !loadError && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatTile label="Total sessions" value={totals?.total ?? 0}
                      hint={`${totals?.devices ?? 0} devices`} />
            {OUTCOMES.map((outcome) => (
              <StatTile
                key={outcome.key}
                label={outcome.label}
                value={totals?.[outcome.key] ?? 0}
                color={outcome.color}
                hint={outcome.key === "unknown" ? `${unknownPct}% of sessions` : undefined}
              />
            ))}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl px-5 py-4">
            <h2 className="text-sm font-semibold text-slate-900">Sessions per day</h2>
            <p className="text-xs text-slate-500 mb-3">
              {totals?.deviceAbortFlagged ?? 0} of these sessions also carry a pump-side
              abort — counted separately, see below.
            </p>
            {/* ResponsiveContainer needs a sized parent or it collapses to zero. */}
            <div className="h-[320px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={series} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }}
                         tickLine={false} axisLine={{ stroke: "#e2e8f0" }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }}
                         tickLine={false} axisLine={false} />
                  <Tooltip content={<SeriesTooltip />} cursor={{ fill: "#f1f5f9" }} />
                  <Legend verticalAlign="bottom" height={32}
                          wrapperStyle={{ fontSize: 12 }} />
                  {OUTCOMES.map((outcome, index) => (
                    <Bar
                      key={outcome.key}
                      dataKey={outcome.key}
                      name={outcome.label}
                      stackId="outcome"
                      fill={outcome.color}
                      // 2px surface gap between stacked segments, and rounded
                      // ends only on the topmost segment.
                      stroke="#ffffff"
                      strokeWidth={2}
                      radius={index === OUTCOMES.length - 1 ? [4, 4, 0, 0] : 0}
                      maxBarSize={44}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* The caveats are not optional. Each of these has already caused a
              wrong reading of this data at least once. */}
          <div className="bg-white border border-slate-200 rounded-xl px-5 py-4">
            <h2 className="text-sm font-semibold text-slate-900 mb-2">How to read this</h2>
            <ul className="text-xs text-slate-600 space-y-1.5 list-disc pl-4">
              <li>
                Dates are <strong>upload dates</strong>, not therapy dates. A session
                running near midnight UTC is counted on the day its log arrived.
              </li>
              <li>
                <strong>Unknown</strong> means the outcome could not be determined —
                most often the phone never uploaded its log. It is <strong>not</strong> a
                failed therapy.
              </li>
              <li>
                The pump log commonly ends with &ldquo;Therapy aborted&rdquo; even when the
                phone reports a completed therapy. That is counted separately as a
                pump-side abort and never changes the outcome above.
              </li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
